/**
 * Overlay live CRM-store data onto a proposal trigger's point-in-time snapshot.
 *
 * Trigger data arrives from the `?include=trigger` endpoint as a flat, captured
 * `event` payload. For non-email trigger entities the same fields also live on
 * the entity's store `CrmObject` (kept fresh by WebSocket), so we prefer the
 * live store value and fall back to the snapshot while the store loads / for
 * fields the store doesn't carry. Email threads are NOT resolved here — their
 * body/participants live in timeline events, not entity properties.
 */

import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import type { CrmEventTriggerData } from '@/lib/proposals/core/types';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

/**
 * Flatten a store object's typed `properties` bag to the `{ key: rawValue }`
 * shape the trigger `event` payload uses. Skips `undefined` values so the
 * overlay never blanks out a field the snapshot has but the store lacks.
 */
function flattenStoreProperties(
  properties: CrmObject['properties']
): Record<string, unknown> {
  if (!properties) {
    return {};
  }
  const flat: Record<string, unknown> = {};
  for (const [key, propValue] of Object.entries(properties)) {
    const value = getPropertyValue(propValue);
    if (value !== undefined) {
      flat[key] = value;
    }
  }
  return flat;
}

/**
 * Return a copy of `trigger` with the live store object's properties (and
 * display name) overlaid on its `event` snapshot. Store values win; the
 * snapshot fills any gaps. Returns `trigger` unchanged when there's no store
 * object (not yet loaded, or entity absent). `associated_meeting` and every
 * other field on the trigger are preserved.
 */
export function resolveTriggerFromStore(
  trigger: CrmEventTriggerData,
  storeObject: CrmObject | undefined
): CrmEventTriggerData {
  if (!storeObject) {
    return trigger;
  }
  return {
    ...trigger,
    crm_event: {
      ...trigger.crm_event,
      display_name:
        getDisplayName(storeObject, '') || trigger.crm_event.display_name,
      event: {
        ...trigger.crm_event.event,
        ...flattenStoreProperties(storeObject.properties),
      },
    },
  };
}
